import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Campaigns } from '@/components/pages/channels/campaigns';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import { useChannelColumns } from './campaigns/columns';
import { SourcingOpenings } from './sourcing-openings';
import { JobPostingCard } from './job-posting-card';

export function ChannelsSection() {
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, displayPrefs } = useOpeningContext();
  const { canPostToJobBoards } = useAuthContext();

  // Kick this fetch off BEFORE `useOpeningDetails` so it fires in parallel
  // with the funnel-details API instead of waiting for the suspense to
  // resolve below.
  const { data, isError } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.list,
      openingSlug,
      hiringGoalId,
      displayPrefs.showAggregateHires,
      displayPrefs.showAggregateSpend,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug,
        hiringGoalId,
        displayPrefs.showAggregateHires,
        displayPrefs.showAggregateSpend,
      ),
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const {
    opening: { isActive, isSourcingEnabled },
    hiringGoal,
  } = useOpeningDetails();

  const showJobPostingCard =
    canPostToJobBoards && isActive && isSourcingEnabled;

  const columns = useChannelColumns({
    brand: data.brand_slug ?? 'no_brand',
    location: data.location_id,
    funnel: data.opening_id,
  });

  const campaigns = useMemo(() => data.active_channels, [data]);

  const activeCampaigns = useMemo(
    () =>
      campaigns.filter(
        campaign =>
          campaign.status === 'active' || campaign.status === 'stopped',
      ),
    [campaigns],
  );

  const isCurrentHiringGoal = hiringGoal?.is_current ?? false;

  const ongoingCampaigns = useMemo(() => {
    const other =
      isCurrentHiringGoal && data.other_channel ? [data.other_channel] : [];
    return [...activeCampaigns, ...other];
  }, [activeCampaigns, data.other_channel, isCurrentHiringGoal]);

  const scheduledCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.status === 'scheduled'),
    [campaigns],
  );

  const completedCampaigns = useMemo(() => {
    const filteredCompleted = campaigns.filter(
      campaign => campaign.status === 'completed',
    );

    const other =
      !isCurrentHiringGoal && data.other_channel ? [data.other_channel] : [];
    return [...filteredCompleted, ...other];
  }, [campaigns, isCurrentHiringGoal, data.other_channel]);

  return (
    <>
      <Suspense fallback={<SourcingOpenings.Skeleton />}>
        <SourcingOpenings />
      </Suspense>

      {ongoingCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Ongoing campaigns"
              description="Section: live channel campaigns for this opening"
            />
          </Typography>
          <Campaigns campaigns={ongoingCampaigns} columns={columns} />
        </>
      )}

      {scheduledCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Scheduled campaigns"
              description="Section: future-dated campaigns not started yet"
            />
          </Typography>
          <Campaigns campaigns={scheduledCampaigns} columns={columns} />
        </>
      )}

      {completedCampaigns.length > 0 && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage
              defaultMessage="Completed campaigns"
              description="Section: finished campaigns for historical reference"
            />
          </Typography>
          <Campaigns campaigns={completedCampaigns} columns={columns} />
        </>
      )}

      {showJobPostingCard && <JobPostingCard />}
    </>
  );
}

ChannelsSection.Skeleton = function ChannelsSectionSkeleton() {
  return (
    <>
      <SourcingOpenings.Skeleton />

      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
      <Skeleton className="h-3 w-40 shrink-0" />
      <Campaigns.Skeleton />
    </>
  );
};
